import { DatePicker, Flex, Form, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';

import DateTime from 'utils/datetime';

import type { Dayjs } from 'dayjs';
import { type ComponentProps, useMemo } from 'react';

import useAddNotesMutation, {
  type AddNotesPayload,
} from 'screens/task-management/hooks/mutations/useAddNotesMutation';
import type { TaskDetail } from 'screens/task-management/types';

interface TaskReschedulerFormProps {
  onRescheduleSuccess?: () => void;
  onCancel?: () => void;
  task: TaskDetail;
}

type TaskReschedulerFormValues = {
  plannedDate: Dayjs | null;
  notes: string | null;
};

type DateMinMaxType =
  | ComponentProps<typeof DatePicker>['minDate']
  | ComponentProps<typeof DatePicker>['maxDate'];

const TaskReschedulerForm = ({
  task,
  onRescheduleSuccess,
  onCancel,
}: TaskReschedulerFormProps) => {
  const { taskId, plannedDate, startDate, endDate } = task || {};
  const { t } = useTranslation();
  const [form] = Form.useForm<TaskReschedulerFormValues>();

  const rescheduleTaskMutation = useAddNotesMutation();

  const maxDatePickerValue = useMemo(
    () => DateTime.toDayjs(endDate) as DateMinMaxType,
    [endDate],
  );
  const minDatePickerValue = useMemo(
    () => DateTime.toDayjs(startDate) as DateMinMaxType,
    [startDate],
  );

  const handleFormSubmit = (values: TaskReschedulerFormValues) => {
    const payload: AddNotesPayload = {
      taskAssignmentId: taskId,
      type: 'RESCHEDULE',
      plannedDate: DateTime.getIsoWithTimeAsStart(
        DateTime.toDayjs(values?.plannedDate)?.endOf('day'),
      ),
      notes: values?.notes || '',
    };

    rescheduleTaskMutation.mutateAsync(payload, {
      onSuccess: () => {
        message.success(t('taskManagement.labels.rescheduleSuccess'));
        onRescheduleSuccess?.();
      },
      onError: (error) => {
        message.error(error?.message || t('common.somethingWrong'));
      },
    });
  };

  const isDateDisabled = (date: Dayjs) => {
    const start = DateTime.toDayjs(startDate);
    const end = DateTime.toDayjs(endDate);

    return (
      date &&
      (DateTime.isBefore(date as Dayjs, start, 'day') ||
        DateTime.isAfter(date as Dayjs, end, 'day'))
    );
  };

  return (
    <Form
      className="w-100 task-rescheduler-form"
      form={form}
      layout="vertical"
      initialValues={{
        plannedDate: DateTime.toDayjs(plannedDate),
        notes: null,
      }}
      onFinish={handleFormSubmit}
    >
      <Form.Item
        className="w-100 mb-3"
        name="plannedDate"
        label={t('taskManagement.labels.newDate')}
        rules={[
          {
            required: true,
            message: t('common.fieldRequired', {
              value: t('taskManagement.labels.newDate'),
            }),
          },
          {
            type: 'date',
            validator(_, value) {
              if (isDateDisabled(value as Dayjs)) {
                return Promise.reject(t('common.dateOutsideTaskWindow'));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <DatePicker
          format="MMM DD, YYYY"
          className="w-full mb-1"
          disabledDate={(date) => isDateDisabled(date as Dayjs)}
          minDate={minDatePickerValue}
          maxDate={maxDatePickerValue}
        />
      </Form.Item>

      <Form.Item
        className="w-100 mt-3"
        name="notes"
        label={t('common.notes')}
        rules={[
          {
            required: true,
            message: t('common.fieldRequired', {
              value: t('common.notes'),
            }),
          },
          { max: 250, message: t('common.maxCharacters', { value: 250 }) },
        ]}
      >
        <TextArea showCount rows={4} className="w-full mb-1" maxLength={250} />
      </Form.Item>

      <Flex align="center" gap={8} className="mt-2">
        <Button type="default" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          trackerLabel={`Task Rescheduler Form Submit - Task ID: ${taskId}`}
          loading={rescheduleTaskMutation.isPending}
        >
          {t('taskManagement.labels.reschedule')}
        </Button>
      </Flex>
    </Form>
  );
};

export default TaskReschedulerForm;
